import { ComponentRef, NgModuleRef, Type, ViewContainerRef, inject } from '@angular/core';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { LoaderStylesService } from '@frontend/loaders';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { ModuleLoaderService } from '@frontend/sports/deferred/feature/module-loader';
import { LazyClientConfigBase } from '@frontend/vanilla/core';
import { assign } from 'lodash-es';
import { Observable, Subject, catchError, firstValueFrom, from, isObservable, map, of, takeUntil } from 'rxjs';

export interface ComponentProxy<T = never> {
    /**
     * Will emit at most once.
     *
     * @memberof ComponentProxy
     */
    create(container: ViewContainerRef): Observable<void>;

    destroy(): void;

    update(data: Partial<T>): void;

    subscribeToOutputs(events: ComponentOutput<T>): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComponentLoadingInfo<T> = () => Promise<{ module: NgModuleRef<any>; type: Type<T> }>;

type ComponentOutput<T> = {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    readonly [P in keyof Partial<T>]: Function;
};

class ComponentProxyRef<T> implements ComponentProxy<T> {
    data: Partial<T> = {};
    component?: ComponentRef<T>;

    private destroy$ = new Subject<void>();
    private outputs?: ComponentOutput<T>;

    constructor(
        private componentLoadingInfo: ComponentLoadingInfo<T>,
        private logger: SportsRemoteLogger,
    ) {}

    create(container: ViewContainerRef): Observable<void> {
        return from(this.componentLoadingInfo()).pipe(
            map((compLoadingInfo) => {
                container.clear();

                this.component = container.createComponent(compLoadingInfo.type, {
                    environmentInjector: compLoadingInfo.module,
                });

                if (this.data) {
                    this.update(this.data);
                }

                if (this.outputs) {
                    this.subscribeToOutputs(this.outputs);
                    this.outputs = undefined;
                }
            }),
            catchError((e: unknown) => {
                this.logger.error(e);

                return of();
            }),
            takeUntil(this.destroy$),
        );
    }

    update(data: Partial<T>): void {
        this.data = assign(this.data, data);

        if (this.component) {
            // eslint-disable-next-line guard-for-in
            for (const key in this.data) {
                this.component.setInput(key, this.data[key]);
            }

            this.component.changeDetectorRef.detectChanges();
        }
    }

    subscribeToOutputs(events: ComponentOutput<T>): void {
        if (!this.component) {
            this.outputs = events;

            return;
        }

        const keys = Object.keys(events);
        for (const key of keys) {
            //@ts-expect-error allow dynamic accessor, please don't use this, instead, use typing.
            const output = this.component.instance[key];
            if (isObservable(output)) {
                //@ts-expect-error allow dynamic accessor, please don't use this, instead, use typing.
                output.pipe(takeUntil(this.destroy$)).subscribe(events[key]);
            }
        }
    }

    destroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

export abstract class ComponentLoaderService<T> {
    private logger: SportsRemoteLogger;

    private readonly stylesLazyLoadingService = inject(LoaderStylesService);

    constructor(
        private moduleLoaderService: ModuleLoaderService,
        private loggerFactory: LoggerFactory,
    ) {
        this.logger = this.loggerFactory.getLogger('ComponentLoaderService');
    }

    abstract readonly moduleName: string;

    abstract getModule(): Promise<Type<T>>;

    protected getComponentProxy<TComponent>(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeSelector: (module: T) => Type<any>,
        styleSheet?: string,
        ...lazyConfigs: LazyClientConfigBase[]
    ): ComponentProxyRef<TComponent> {
        const logger = this.loggerFactory.getLogger('ComponentLoaderService');

        const componentLoadingInfo = async () => {
            try {
                const stylesPromise = styleSheet ? this.stylesLazyLoadingService.loadStyles([{ id: styleSheet, type: 'theme' }]) : Promise.resolve();
                const [module] = await Promise.all([
                    firstValueFrom(this.moduleLoaderService.loadModule<T>(this.moduleName, this.getModule, ...lazyConfigs)),
                    stylesPromise,
                ]);
                const type = typeSelector(module.instance);

                return { module, type };
            } catch (error) {
                this.logger.error(error);
                throw error;
            }
        };

        return new ComponentProxyRef(componentLoadingInfo, logger);
    }
}
